import React, { useState, useEffect } from 'react';

const COMMON_CONDITIONS = [
  'Diabetes Type 1', 'Diabetes Type 2', 'High Blood Pressure',
  'Asthma', 'Heart Disease', 'Thyroid Disorder', 'Arthritis',
  'Anemia', 'Kidney Disease', 'Liver Disease', 'Epilepsy',
  'Depression', 'Anxiety', 'PCOD/PCOS', 'Migraine', 'Other'
];

const SEVERITY_CONFIG = {
  mild: { color: 'text-green-600', bg: 'bg-green-50', border: 'border-green-200', bar: '#22c55e', label: 'Mild' },
  moderate: { color: 'text-amber-600', bg: 'bg-amber-50', border: 'border-amber-200', bar: '#f59e0b', label: 'Moderate' },
  severe: { color: 'text-red-600', bg: 'bg-red-50', border: 'border-red-200', bar: '#ef4444', label: 'Severe' },
};

function MobileConditions({ user, showAdd: showAddProp, onAddClose }) {
  const [conditions, setConditions] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ conditionName: '', severity: 'moderate', diagnosedDate: '', notes: '', custom: '' });
  const [message, setMessage] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  useEffect(() => { fetchConditions(); }, []); // eslint-disable-line

  useEffect(() => {
    if (showAddProp) { setShowAdd(true); onAddClose && onAddClose(); }
  }, [showAddProp]); // eslint-disable-line

  const fetchConditions = async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/Conditions/${user.id}`);
      setConditions(await res.json());
    } catch { }
  };

  const handleAdd = async () => {
    const name = form.conditionName === 'Other' ? form.custom : form.conditionName;
    if (!name.trim()) { setMessage('Please select or enter a condition.'); return; }
    setLoading(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/Conditions`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ patientId: user.id, conditionName: name, severity: form.severity, diagnosedDate: form.diagnosedDate || null, notes: form.notes })
      });
      if (res.ok) {
        setMessage('✅ Condition added!');
        setForm({ conditionName: '', severity: 'moderate', diagnosedDate: '', notes: '', custom: '' });
        setShowAdd(false); fetchConditions();
      }
    } catch { setMessage('❌ Cannot connect to server.'); }
    setLoading(false);
  };

  const handleToggle = async (id) => {
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/Conditions/toggle/${id}`, { method: 'PATCH' });
      const data = await res.json();
      setConditions(prev => prev.map(c => c.id === id ? { ...c, isActive: data.isActive } : c));
    } catch { }
  };

  const handleDelete = async (id) => {
    try {
      await fetch(`${process.env.REACT_APP_API_URL}/api/Conditions/${id}`, { method: 'DELETE' });
      setConditions(prev => prev.filter(c => c.id !== id));
      setDeleteConfirm(null);
    } catch { }
  };

  const active = conditions.filter(c => c.isActive);
  const inactive = conditions.filter(c => !c.isActive);

  return (
    <div className="px-4 py-4" style={{ fontFamily: 'Inter, sans-serif' }}>

      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div className="flex-1 pr-3">
          <h2 className="text-3xl font-black text-[#12172b]">Conditions</h2>
          <p className="text-gray-400 text-sm mt-1">Track and manage your health conditions</p>
        </div>
        {/* Decorative shield + EKG icon */}
        <svg width="108" height="98" viewBox="0 0 130 118" className="shrink-0 -mt-1">
          <path d="M8,68 L38,68 L46,52 L54,84 L64,32 L74,90 L82,68 L122,68"
            fill="none" stroke="#dbe1f7" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M65 6 L96 18 L96 50 C96 76 82 92 65 100 C48 92 34 76 34 50 L34 18 Z"
            fill="#e9ecfa" />
          <rect x="56" y="42" width="18" height="32" rx="4" fill="#7c93ea" />
          <rect x="49" y="49" width="32" height="18" rx="4" fill="#7c93ea" />
        </svg>
      </div>

      {/* Add Condition Form */}
      {showAdd && (
        <div className="bg-white rounded-3xl border border-gray-200 p-4 mb-4 shadow-sm">
          <h3 className="font-black text-[#12172b] text-base mb-3">Add New Condition</h3>
          {message && (
            <div className={`p-2 rounded-xl text-xs mb-3 ${message.includes('✅') ? 'bg-green-50 text-green-600' : 'bg-red-50 text-red-600'}`}>{message}</div>
          )}
          <div className="space-y-3">
            <div>
              <label className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1 block">Condition</label>
              <select value={form.conditionName} onChange={e => setForm({ ...form, conditionName: e.target.value })}
                className="w-full px-3 py-2.5 border border-gray-200 rounded-2xl text-sm bg-gray-50 focus:outline-none focus:border-[#3457dd]">
                <option value="">Select condition...</option>
                {COMMON_CONDITIONS.map(c => <option key={c}>{c}</option>)}
              </select>
            </div>
            {form.conditionName === 'Other' && (
              <input type="text" placeholder="Enter condition name..." value={form.custom}
                onChange={e => setForm({ ...form, custom: e.target.value })}
                className="w-full px-3 py-2.5 border border-gray-200 rounded-2xl text-sm bg-gray-50 focus:outline-none focus:border-[#3457dd]" />
            )}
            <div>
              <label className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1 block">Severity</label>
              <div className="flex gap-2">
                {Object.entries(SEVERITY_CONFIG).map(([key, val]) => (
                  <button key={key} onClick={() => setForm({ ...form, severity: key })}
                    className={`flex-1 py-2 rounded-xl text-xs font-bold border transition-all ${form.severity === key ? `${val.bg} ${val.border} ${val.color}` : 'bg-gray-50 border-gray-200 text-gray-400'}`}>
                    {val.label}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1 block">Diagnosed Date (optional)</label>
              <input type="date" value={form.diagnosedDate} onChange={e => setForm({ ...form, diagnosedDate: e.target.value })}
                className="w-full px-3 py-2.5 border border-gray-200 rounded-2xl text-sm bg-gray-50 focus:outline-none focus:border-[#3457dd]" />
            </div>
            <div>
              <label className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1 block">Notes (optional)</label>
              <textarea placeholder="Any additional notes..." value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })}
                className="w-full px-3 py-2.5 border border-gray-200 rounded-2xl text-sm bg-gray-50 focus:outline-none focus:border-[#3457dd] resize-none" rows={2} />
            </div>
            <div className="flex gap-2">
              <button onClick={() => { setShowAdd(false); setMessage(''); }}
                className="flex-1 py-3 border-2 border-gray-200 text-gray-600 font-bold rounded-2xl text-sm">Cancel</button>
              <button onClick={handleAdd} disabled={loading}
                className="flex-1 py-3 text-white font-bold rounded-2xl text-sm disabled:opacity-50 active:scale-95"
                style={{ background: '#3457dd' }}>
                {loading ? '⏳ Saving...' : 'Add Condition'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Active Conditions */}
      {active.length > 0 && (
        <div className="mb-4">
          <p className="text-xs font-black uppercase tracking-widest mb-3" style={{ color: '#3457dd' }}>Active Conditions ({active.length})</p>
          <div className="space-y-3">
            {active.map(c => {
              const sev = SEVERITY_CONFIG[c.severity] || SEVERITY_CONFIG.moderate;
              return (
                <div key={c.id} className="bg-white rounded-2xl shadow-sm p-4 flex items-start gap-3 relative overflow-hidden"
                  style={{ borderLeft: `4px solid ${sev.bar}` }}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-black text-[#12172b] text-lg">{c.conditionName}</p>
                      <span className={`text-xs font-bold px-2.5 py-0.5 rounded-full ${sev.bg} ${sev.color}`}>{sev.label}</span>
                    </div>
                    {c.diagnosedDate && (
                      <div className="flex items-center gap-1.5 mt-1">
                        <span className="material-symbols-outlined text-gray-400" style={{ fontSize: '15px' }}>calendar_today</span>
                        <p className="text-sm text-gray-400">Diagnosed: {new Date(c.diagnosedDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</p>
                      </div>
                    )}
                    {c.notes && <p className="text-xs text-gray-500 mt-1.5 italic">"{c.notes}"</p>}
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <button onClick={() => handleToggle(c.id)} className="w-10 h-5 rounded-full relative transition-colors" style={{ background: '#3457dd' }}>
                      <div className="w-4 h-4 bg-white rounded-full shadow absolute top-0.5 left-5 transition-all"></div>
                    </button>
                    <button onClick={() => setDeleteConfirm(c)} className="w-9 h-9 flex items-center justify-center text-gray-400 bg-gray-100 hover:text-red-400 hover:bg-red-50 rounded-full transition-colors">
                      <span className="material-symbols-outlined" style={{ fontSize: '17px' }}>delete</span>
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Inactive Conditions */}
      {inactive.length > 0 && (
        <div className="mb-4">
          <p className="text-xs font-black text-gray-400 uppercase tracking-widest mb-3">Past Conditions ({inactive.length})</p>
          <div className="space-y-2">
            {inactive.map(c => (
              <div key={c.id} className="bg-white rounded-2xl border border-gray-100 p-4 shadow-sm opacity-60">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-gray-500 text-sm line-through">{c.conditionName}</p>
                    <p className="text-xs text-gray-400">Inactive</p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <button onClick={() => handleToggle(c.id)} className="w-10 h-5 bg-gray-200 rounded-full relative">
                      <div className="w-4 h-4 bg-white rounded-full shadow absolute top-0.5 left-0.5"></div>
                    </button>
                    <button onClick={() => setDeleteConfirm(c)} className="w-9 h-9 flex items-center justify-center text-gray-400 bg-gray-100 hover:text-red-400 hover:bg-red-50 rounded-full transition-colors">
                      <span className="material-symbols-outlined" style={{ fontSize: '17px' }}>delete</span>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Privacy note */}
      {conditions.length > 0 && (
        <div className="flex items-center gap-3 rounded-2xl p-4 mt-2" style={{ background: '#f2f3fb' }}>
          <div className="w-11 h-11 rounded-full bg-white flex items-center justify-center shrink-0">
            <span className="material-symbols-outlined" style={{ fontSize: '19px', color: '#3457dd' }}>lock</span>
          </div>
          <div className="flex-1">
            <p className="text-sm font-black text-[#12172b]">Your health data is private and secure</p>
            <p className="text-xs text-gray-400 mt-0.5 leading-snug">We use industry-standard encryption to keep your information safe.</p>
          </div>
          <span className="material-symbols-outlined shrink-0" style={{ fontSize: '30px', color: '#c9cff0' }}>verified_user</span>
        </div>
      )}

      {/* Empty State */}
      {conditions.length === 0 && !showAdd && (
        <div className="bg-white rounded-3xl px-6 py-10 text-center border border-gray-100 shadow-sm mt-4">
          <span className="material-symbols-outlined" style={{ fontSize: '80px', fontVariationSettings: '"FILL" 1', color: '#c9cff0' }}>shield</span>
          <p className="text-[#12172b] font-black text-2xl mb-2 mt-2">No conditions added yet</p>
          <p className="text-gray-400 text-base mb-7">Track your health conditions here</p>
          <button onClick={() => setShowAdd(true)}
            className="inline-flex items-center gap-2 px-8 py-3.5 text-white font-bold rounded-full text-base active:scale-95"
            style={{ background: '#3457dd' }}>
            <span className="material-symbols-outlined" style={{ fontSize: '18px' }}>add</span>Add Condition
          </button>
        </div>
      )}

      {/* Delete Confirmation */}
      {deleteConfirm && (
        <div className="fixed inset-0 z-[200] bg-black/50 flex items-end">
          <div className="w-full bg-white rounded-t-3xl p-6 shadow-2xl">
            <div className="flex flex-col items-center text-center mb-5">
              <div className="w-14 h-14 bg-red-50 rounded-full flex items-center justify-center mb-3">
                <span className="material-symbols-outlined text-red-500 text-3xl" style={{ fontVariationSettings: '"FILL" 1' }}>delete</span>
              </div>
              <h3 className="font-black text-[#12172b] text-lg">Delete Condition?</h3>
              <p className="text-gray-400 text-sm mt-1">"{deleteConfirm.conditionName}" will be permanently deleted.</p>
            </div>
            <div className="flex gap-3">
              <button onClick={() => setDeleteConfirm(null)} className="flex-1 py-3 border-2 border-gray-200 text-gray-600 font-bold rounded-2xl text-sm">Cancel</button>
              <button onClick={() => handleDelete(deleteConfirm.id)} className="flex-1 py-3 bg-red-500 text-white font-bold rounded-2xl text-sm">Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default MobileConditions;